import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext.jsx';
import { useSettings } from './SettingsContext.jsx';
import { useUI } from '../components/ToastProvider.jsx';

const C = '#22c55e';
// Used only if the credentials endpoint is unreachable (keeps easy-network calls working).
const FALLBACK_ICE = [{ urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] }];

const CallContext = createContext(null);
export const useCall = () => useContext(CallContext);

// App-level call manager: keeps a dedicated socket alive on EVERY screen so that
// incoming calls are received no matter which tab the user is on. Owns the full
// WebRTC lifecycle and renders the call overlay above everything.
export function CallProvider({ children }) {
  const { token, user, apiFetch } = useAuth();
  const { t } = useSettings();
  const { toast } = useUI();

  const socketRef = useRef(null);
  const [call, setCall] = useState(null); // { state, peerUserId, peerUsername, conversationId, muted, cameraOff, video }
  const callRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const facingRef = useRef('user');       // current camera: 'user' (front) | 'environment' (back)
  const switchingRef = useRef(false);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const pendingIceRef = useRef([]);
  const pendingOfferRef = useRef(null);
  const callTimerRef = useRef(null);
  const dropTimerRef = useRef(null);   // grace timer for transient 'disconnected'
  const [callSeconds, setCallSeconds] = useState(0);
  useEffect(() => { callRef.current = call; }, [call]);

  const cleanupCall = useCallback(() => {
    try { pcRef.current?.close(); } catch {}
    pcRef.current = null;
    try { localStreamRef.current?.getTracks().forEach(tr => tr.stop()); } catch {}
    localStreamRef.current = null;
    remoteStreamRef.current = null;
    facingRef.current = 'user';
    setLocalStream(null);
    setRemoteStream(null);
    pendingIceRef.current = [];
    pendingOfferRef.current = null;
    if (callTimerRef.current) { clearInterval(callTimerRef.current); callTimerRef.current = null; }
    if (dropTimerRef.current) { clearTimeout(dropTimerRef.current); dropTimerRef.current = null; }
    setCallSeconds(0);
  }, []);

  const endCall = useCallback((notifyPeer = true) => {
    const c = callRef.current;
    if (notifyPeer && c?.peerUserId) socketRef.current?.emit('call:hangup', { to: c.peerUserId });
    cleanupCall();
    setCall(null);
  }, [cleanupCall]);

  const startCallTimer = () => {
    if (callTimerRef.current) clearInterval(callTimerRef.current);
    callTimerRef.current = setInterval(() => setCallSeconds(s => s + 1), 1000);
  };

  const iceServersRef = useRef(null);
  const iceExpiryRef = useRef(0);

  // Fetch short-lived TURN credentials from the backend and cache them until
  // shortly before they expire. Falls back to public STUN if unreachable.
  const getIceServers = async () => {
    if (iceServersRef.current && Date.now() < iceExpiryRef.current) return iceServersRef.current;
    try {
      const res = await apiFetch('/turn/credentials');
      if (res?.ok) {
        const data = await res.json();
        if (Array.isArray(data.iceServers) && data.iceServers.length) {
          iceServersRef.current = data.iceServers;
          iceExpiryRef.current = Date.now() + Math.max(60, (data.ttl || 3600) - 300) * 1000;
          return data.iceServers;
        }
      }
    } catch { /* ignore — use fallback */ }
    return FALLBACK_ICE;
  };

  const makePeer = (peerUserId, iceServers) => {
    const pc = new RTCPeerConnection({ iceServers: iceServers || FALLBACK_ICE });
    pc.onicecandidate = (e) => { if (e.candidate) socketRef.current?.emit('call:ice', { to: peerUserId, candidate: e.candidate }); };
    pc.ontrack = (e) => { remoteStreamRef.current = e.streams[0]; setRemoteStream(e.streams[0]); };
    pc.onconnectionstatechange = () => {
      const st = pc.connectionState;
      if (st === 'connected') {
        if (dropTimerRef.current) { clearTimeout(dropTimerRef.current); dropTimerRef.current = null; }
        setCall(c => c && c.state !== 'connected' ? { ...c, state: 'connected' } : c);
        startCallTimer();
      }
      // 'failed'/'closed' are unrecoverable → end immediately. 'disconnected' is
      // usually a brief network blip that ICE recovers from, so wait a few seconds
      // and only end if it hasn't come back.
      if (st === 'failed' || st === 'closed') { if (callRef.current) endCall(false); }
      if (st === 'disconnected') {
        if (dropTimerRef.current) clearTimeout(dropTimerRef.current);
        dropTimerRef.current = setTimeout(() => {
          dropTimerRef.current = null;
          if (callRef.current && pcRef.current && pcRef.current.connectionState !== 'connected') endCall(false);
        }, 7000);
      }
    };
    pcRef.current = pc;
    return pc;
  };

  // Caller: start a 1-1 call (audio or video). `conv` must carry display.otherId + username.
  const startCall = async (conv, video = false) => {
    if (callRef.current) return;
    const peerUserId = conv?.display?.otherId;
    const peerUsername = conv?.display?.username || conv?.display?.title;
    if (!peerUserId) { toast(t('call_only_1on1'), { type:'error' }); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: video ? { facingMode: 'user' } : false });
      localStreamRef.current = stream; setLocalStream(stream);
      const iceServers = await getIceServers();
      const pc = makePeer(peerUserId, iceServers);
      stream.getTracks().forEach(tr => pc.addTrack(tr, stream));
      const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: video });
      await pc.setLocalDescription(offer);
      setCall({ state: 'calling', peerUserId, peerUsername, conversationId: conv.id, muted: false, cameraOff: false, video, facing: 'user' });
      socketRef.current?.emit('call:invite', { to: peerUserId, conversationId: conv.id, sdp: offer, video });
    } catch (e) {
      cleanupCall(); setCall(null);
      toast(video ? t('call_cam_denied') : t('call_mic_denied'), { type:'error' });
    }
  };

  const acceptCall = async () => {
    const c = callRef.current;
    if (!c || c.state !== 'incoming' || !pendingOfferRef.current) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: c.video ? { facingMode: 'user' } : false });
      localStreamRef.current = stream; setLocalStream(stream);
      const iceServers = await getIceServers();
      const pc = makePeer(c.peerUserId, iceServers);
      stream.getTracks().forEach(tr => pc.addTrack(tr, stream));
      await pc.setRemoteDescription(new RTCSessionDescription(pendingOfferRef.current));
      pendingOfferRef.current = null;
      for (const cand of pendingIceRef.current) { try { await pc.addIceCandidate(cand); } catch {} }
      pendingIceRef.current = [];
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      socketRef.current?.emit('call:answer', { to: c.peerUserId, sdp: answer });
      setCall(cur => cur && { ...cur, state: 'connecting' });
    } catch (e) {
      socketRef.current?.emit('call:reject', { to: c.peerUserId });
      cleanupCall(); setCall(null);
      toast(c.video ? t('call_cam_denied') : t('call_mic_denied'), { type:'error' });
    }
  };

  const rejectCall = () => {
    const c = callRef.current;
    if (c?.peerUserId) socketRef.current?.emit('call:reject', { to: c.peerUserId });
    cleanupCall(); setCall(null);
  };

  const toggleMute = () => {
    const s = localStreamRef.current; if (!s) return;
    const track = s.getAudioTracks()[0];
    if (track) { track.enabled = !track.enabled; setCall(c => c && { ...c, muted: !track.enabled }); }
  };
  const toggleCamera = () => {
    const s = localStreamRef.current; if (!s) return;
    const track = s.getVideoTracks()[0];
    if (track) { track.enabled = !track.enabled; setCall(c => c && { ...c, cameraOff: !track.enabled }); }
  };
  // Flip between front ('user') and back ('environment') camera mid-call by grabbing a
  // fresh video track and hot-swapping it into the peer connection (sender.replaceTrack).
  const switchCamera = async () => {
    const c = callRef.current;
    if (!c?.video || switchingRef.current) return;
    const pc = pcRef.current, s = localStreamRef.current;
    if (!pc || !s) return;
    switchingRef.current = true;
    const next = facingRef.current === 'user' ? 'environment' : 'user';
    let newStream = null;
    try {
      try { newStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { exact: next } } }); }
      catch { newStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: next } }); }
    } catch { switchingRef.current = false; return; }
    const newTrack = newStream.getVideoTracks()[0];
    if (!newTrack) { switchingRef.current = false; return; }
    newTrack.enabled = !c.cameraOff;
    const sender = pc.getSenders().find(snd => snd.track && snd.track.kind === 'video');
    if (sender) { try { await sender.replaceTrack(newTrack); } catch {} }
    const oldVideo = s.getVideoTracks()[0];
    if (oldVideo) { try { s.removeTrack(oldVideo); oldVideo.stop(); } catch {} }
    const merged = new MediaStream([...s.getAudioTracks(), newTrack]);
    localStreamRef.current = merged;
    facingRef.current = next;
    setLocalStream(merged);
    setCall(cur => cur && { ...cur, facing: next });
    switchingRef.current = false;
  };

  // Dedicated always-on socket for call signaling (separate from the chat socket).
  useEffect(() => {
    if (!token) return;
    // socket.io is code-split: load it lazily so the signaling client doesn't
    // sit in the main bundle. Incoming calls still work — we connect right
    // after the chunk arrives (a moment after startup).
    let disposed = false;
    let socket = null;
    import('socket.io-client').then(({ io }) => {
      if (disposed) return;
      socket = io(window.location.origin, { path: '/socket.io', auth: { token } });
      socketRef.current = socket;
      wireSocket(socket);
    }).catch(() => {});

    function wireSocket(socket) {

    socket.on('call:incoming', ({ from, fromUsername, conversationId, sdp, video }) => {
      if (callRef.current || pcRef.current) { socket.emit('call:busy', { to: from }); return; }
      pendingOfferRef.current = sdp;
      pendingIceRef.current = [];
      setCall({ state: 'incoming', peerUserId: from, peerUsername: fromUsername, conversationId, muted: false, cameraOff: false, video: !!video, facing: 'user' });
    });
    socket.on('call:answer', async ({ sdp }) => {
      const pc = pcRef.current; if (!pc) return;
      try {
        await pc.setRemoteDescription(new RTCSessionDescription(sdp));
        for (const c of pendingIceRef.current) { try { await pc.addIceCandidate(c); } catch {} }
        pendingIceRef.current = [];
        setCall(cur => cur && { ...cur, state: 'connecting' });
      } catch {}
    });
    socket.on('call:ice', async ({ candidate }) => {
      const pc = pcRef.current;
      if (pc && pc.remoteDescription && pc.remoteDescription.type) {
        try { await pc.addIceCandidate(candidate); } catch {}
      } else { pendingIceRef.current.push(candidate); }
    });
    socket.on('call:reject', () => { cleanupCall(); setCall(null); });
    socket.on('call:busy', () => { cleanupCall(); setCall(null); toast(t('call_busy'), { type:'error' }); });
    socket.on('call:hangup', () => { cleanupCall(); setCall(null); });

    }

    return () => { disposed = true; try { socket?.disconnect(); } catch {}; socketRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <CallContext.Provider value={{ startCall, inCall: !!call }}>
      {children}
      <CallOverlay call={call} seconds={callSeconds} localStream={localStream} remoteStream={remoteStream}
        onAccept={acceptCall} onReject={rejectCall} onHangup={() => endCall(true)}
        onToggleMute={toggleMute} onToggleCamera={toggleCamera} onSwitchCamera={switchCamera} t={t} />
    </CallContext.Provider>
  );
}

// Full-screen call overlay: incoming (accept/reject), calling (ringing), and in-call.
// Supports audio (avatar UI) and video (remote video full-screen + local PiP).
function CallOverlay({ call, seconds, localStream, remoteStream, onAccept, onReject, onHangup, onToggleMute, onToggleCamera, onSwitchCamera, t }) {
  const remoteVideoRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);
  useEffect(() => { if (remoteVideoRef.current && remoteStream) remoteVideoRef.current.srcObject = remoteStream; }, [remoteStream, call?.video, call?.state]);
  useEffect(() => { if (localVideoRef.current && localStream) localVideoRef.current.srcObject = localStream; }, [localStream, call?.video, call?.state]);
  useEffect(() => { if (remoteAudioRef.current && remoteStream) remoteAudioRef.current.srcObject = remoteStream; }, [remoteStream]);
  if (!call) return null;

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  const status =
    call.state === 'incoming' ? (call.video ? t('call_incoming_video') : t('call_incoming'))
    : call.state === 'calling' ? t('call_calling')
    : call.state === 'connecting' ? t('call_connecting')
    : fmt(seconds);
  const isVideo = !!call.video;
  const showRemoteVideo = isVideo && remoteStream && (call.state === 'connected' || call.state === 'connecting');
  const showLocalVideo = isVideo && localStream && call.state !== 'incoming';

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: showRemoteVideo ? '#000' : 'rgba(6,10,16,0.97)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      {showRemoteVideo && (
        <video ref={remoteVideoRef} autoPlay playsInline
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      )}
      {!isVideo && <audio ref={remoteAudioRef} autoPlay playsInline />}
      {showLocalVideo && (
        <video ref={localVideoRef} autoPlay playsInline muted
          style={{ position: 'absolute', top: 16, right: 16, width: 110, height: 150, objectFit: 'cover', borderRadius: 12, border: '2px solid #ffffff33', transform: call.facing === 'environment' ? 'none' : 'scaleX(-1)', background: '#11161f', opacity: call.cameraOff ? 0.3 : 1 }} />
      )}
      {!showRemoteVideo && (
        <>
          <div style={{ width: 96, height: 96, borderRadius: '50%', background: '#11161f', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 44, marginBottom: 18, border: `2px solid ${C}55` }}>
            {isVideo ? '📹' : '👤'}
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--app-text,#f0ece4)' }}>@{call.peerUsername || ''}</div>
          <div style={{ fontSize: 14, color: call.state === 'connected' ? '#22c55e' : '#8b95a5', marginTop: 6 }}>{status}</div>
        </>
      )}
      {showRemoteVideo && (
        <div style={{ position: 'absolute', top: 18, left: 18, background: 'rgba(0,0,0,0.45)', borderRadius: 10, padding: '6px 12px' }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>@{call.peerUsername || ''}</div>
          <div style={{ fontSize: 12, color: '#cde' }}>{status}</div>
        </div>
      )}
      {call.state === 'incoming' ? (
        <div style={{ display: 'flex', gap: 40, marginTop: 48, zIndex: 2 }}>
          <button onClick={onReject} style={callBtn('#ef4444')}>✕</button>
          <button onClick={onAccept} style={callBtn('#22c55e')}>{isVideo ? '📹' : '📞'}</button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 20, marginTop: showRemoteVideo ? 0 : 48, alignItems: 'center', zIndex: 2, position: showRemoteVideo ? 'absolute' : 'static', bottom: showRemoteVideo ? 40 : 'auto' }}>
          {(call.state === 'connected' || call.state === 'connecting') && (
            <button onClick={onToggleMute} style={callBtn(call.muted ? '#f59e0b' : '#334a')}>{call.muted ? '🔇' : '🎙'}</button>
          )}
          {isVideo && (call.state === 'connected' || call.state === 'connecting' || call.state === 'calling') && (
            <button onClick={onToggleCamera} style={callBtn(call.cameraOff ? '#f59e0b' : '#334a')}>{call.cameraOff ? '📷' : '📹'}</button>
          )}
          {isVideo && !call.cameraOff && (call.state === 'connected' || call.state === 'connecting' || call.state === 'calling') && (
            <button onClick={onSwitchCamera} style={callBtn('#334a')}>🔄</button>
          )}
          <button onClick={onHangup} style={callBtn('#ef4444')}>✕</button>
        </div>
      )}
      {!showRemoteVideo && <div style={{ fontSize: 11, color: '#555', marginTop: 28 }}>{isVideo ? t('call_video') : t('call_voice')}</div>}
    </div>
  );
}
const callBtn = (bg) => ({ width: 64, height: 64, borderRadius: '50%', background: bg, border: 'none', color: '#fff', cursor: 'pointer', fontSize: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' });
